// src/pages/Proposals/FileDialog/forms/common/EntitySelect.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import api from "../../../../../api/api";

function EntitySelect({
  value, // id actual o null
  onChange, // (id|null) => void
  endpoint, // p.ej. "/detectors/"
  label = "Select",
  getOptionLabel, // (opt) => string
  searchParam = "search",
  sx,
  multiple = false,
  refetchOnEndpointChange = false,
}) {
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState(""); // "" hará una primera carga por debounce
  const [loading, setLoading] = useState(false);

  // Control de carreras entre peticiones
  const reqIdRef = useRef(0);
  const ensuredIdRef = useRef(false);
  const optionsRef = useRef(options);
  const prevEndpointRef = useRef(endpoint);

  // ¿Tiene filtro?
  const isFilterEndpoint = endpoint?.includes("?");

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const fetchEntities = useCallback(
    async (q = "") => {
      const myId = ++reqIdRef.current;
      setLoading(true);
      try {
        const url =
          q && q.trim()
            ? `${endpoint}?${encodeURIComponent(
                searchParam,
              )}=${encodeURIComponent(q)}`
            : endpoint;
        const { data } = await api.get(url);
        // Ignora respuestas antiguas
        if (myId !== reqIdRef.current) return;
        const list = Array.isArray(data) ? data : data.results || [];
        setOptions((prev) => {
          // evitar churn si la lista es igual (comparación básica por ids y longitud)
          if (
            prev.length === list.length &&
            prev.every((p, i) => p.id === list[i]?.id)
          ) {
            return prev;
          }
          return list;
        });
      } catch {
        // podrías mostrar error si quieres
      } finally {
        if (myId === reqIdRef.current) setLoading(false);
      }
    },
    [endpoint, searchParam],
  );

  // Carga inicial y búsqueda (debounce)
  useEffect(() => {
    const ensure = async () => {
      if (!value) return;
      if (!endpoint) return;

      // 🔥 MULTIPLE
      if (multiple && Array.isArray(value)) {
        const missingIds = value.filter(
          (id) => !optionsRef.current.some((o) => o.id === id),
        );

        if (missingIds.length === 0) return;

        try {
          // 🔥 IMPORTANTE: SOLO LIST FETCH
          const { data } = await api.get(endpoint);
          const list = Array.isArray(data) ? data : data.results || [];

          const newItems = list.filter((o) => missingIds.includes(o.id));

          setOptions((prev) => {
            const existing = new Set(prev.map((o) => o.id));
            const filtered = newItems.filter((o) => !existing.has(o.id));
            return [...filtered, ...prev];
          });
        } catch (e) {
          console.error("Error fetching missing entities", e);
        }

        return;
      }

      // 🔥 SINGLE
      const id = typeof value === "object" ? value?.id : value;

      if (ensuredIdRef.current === value) return;

      if (optionsRef.current.some((o) => o.id === id)) {
        ensuredIdRef.current = id;
        return;
      }

      try {
        // 🔥 IMPORTANTE: SOLO LIST FETCH + find
        const { data } = await api.get(endpoint);
        const list = Array.isArray(data) ? data : data.results || [];

        const found = list.find((o) => o.id === id);

        if (!found) return;

        setOptions((prev) => {
          if (prev.some((o) => o.id === found.id)) return prev;
          return [found, ...prev];
        });
      } catch (e) {
        console.error("Error fetching entity", e);
      } finally {
        ensuredIdRef.current = value;
      }
    };

    ensure();
  }, [endpoint, value, multiple]);

  useEffect(() => {
    if (input !== "") return;
    if (optionsRef.current.length > 0) return;

    fetchEntities();
  }, [input, fetchEntities]);

  // RECARGA DE OPCIONES
  useEffect(() => {
    if (!refetchOnEndpointChange) return;
    if (!endpoint) return;

    if (prevEndpointRef.current === endpoint) return;

    prevEndpointRef.current = endpoint;

    fetchEntities();
  }, [endpoint, refetchOnEndpointChange, fetchEntities]);

  const selected = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(value)) return [];

      return value
        .map((id) => options.find((o) => o.id === id))
        .filter(Boolean); // 🔥 elimina undefined
    }

    return options.find((o) => o.id === value) || null;
  }, [options, value, multiple]);

  // Comprobar problemas de render
  // useEffect(() => {
  //   console.log("EntitySelect mounted / updated");
  // }, []);
  // useEffect(() => {
  //   console.log("FETCH TRIGGERED", endpoint);
  // }, [endpoint]);
  // useEffect(() => {
  //   console.log("FETCH ENTITIES EXECUTED");
  // }, [endpoint]);

  const safeValue = multiple
    ? Array.isArray(selected)
      ? selected
      : []
    : selected ?? null;

  return (
    <Autocomplete
      size="small"
      multiple={multiple}
      options={options}
      loading={loading}
      value={safeValue}
      onChange={(_, option) => {
        console.log("option:", option);

        if (multiple) {
          onChange(Array.isArray(option) ? option.map((opt) => opt.id) : []);
        } else {
          onChange(option ? option.id : null);
        }
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === "input") setInput(newInput ?? "");
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(opt, val) => {
        if (!opt || !val) return false;
        return opt.id === (val.id ?? val);
      }}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField
          {...params}
          label={label}
          placeholder={`Search...`}
          sx={{ mb: 1, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li {...props} style={{ paddingTop: 4, paddingBottom: 4 }}>
          {getOptionLabel(option)}
        </li>
      )}
    />
  );
}

EntitySelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number), // 🔥 NUEVO
    PropTypes.oneOf([null]),
  ]),
  onChange: PropTypes.func.isRequired,
  // endpoint: PropTypes.string.isRequired,
  label: PropTypes.string,
  getOptionLabel: PropTypes.func.isRequired,
  searchParam: PropTypes.string,
  sx: PropTypes.object,
  multiple: PropTypes.bool,
};

EntitySelect.defaultProps = {
  value: null,
  label: "Select",
  searchParam: "search",
  sx: {},
  multiple: false,
  refetchOnEndpointChange: false,
};

export default React.memo(EntitySelect);
